import React, { memo, useCallback, useMemo, useState } from 'react'

import { useEventCapture } from '@surglogs-app/analytics'
import {
  MfaSetupProgressBar,
  MfaAuthenticatorForm,
  useMfaAbortTimer,
} from '@surglogs-app/auth-mfa'
import { useHandler } from '@surglogs-app/hooks'
import { useAccessManagementAccount } from '@surglogs-app/network'
import { Config } from '@surglogs-app/types'
import { Modal } from '@surglogs-app/ui.modal'

import { useAccountSettingsTranslation } from '../services'

import { AccountSettingsForm } from './AccountSettingsForm'
import { SecurityActions } from './SecurityActions'
import { AccountSettingsModalViewType } from './types'

interface AccountSettingsModalProps {
  hideModal: () => void
  config: Pick<Config, 'reCaptchaKey'>
}

export const AccountSettingsModal = memo(
  ({ hideModal, config }: AccountSettingsModalProps) => {
    const { t } = useAccountSettingsTranslation()
    const { captureEvent } = useEventCapture()
    const [activeView, setActiveView] =
      useState<AccountSettingsModalViewType>(null)

    const { data: user, refetch } = useAccessManagementAccount({})

    const { isLastMinute, timeLeft } = useMfaAbortTimer({
      onTimeout: hideModal,
    })

    const tabView = useCallback(
      ({ currentTab }: { currentTab: number }) =>
        currentTab === 0 ? (
          <AccountSettingsForm {...{ hideModal, user }} />
        ) : (
          <SecurityActions
            {...{
              hideModal,
              setActiveView,
              mfaEnabled: user?.mfa_enabled,
              config,
            }}
          />
        ),
      [hideModal, user, config],
    )

    const onMfaSetupCanceled = useHandler(() => {
      captureEvent('mfa_setup_canceled', { context: 'account' })
      setActiveView(null)
    })

    const onMfaSetupCompleted = useHandler(async () => {
      await refetch()
    })

    const activeInternalView = useMemo(() => {
      switch (activeView) {
        case 'mfa_step1':
        case 'mfa_step2':
          return (
            <MfaAuthenticatorForm
              {...{
                setActiveView,
                activeView,
                t,
                context: 'account',
                onClose: onMfaSetupCanceled,
                onMfaSetupCompleted,
                isLastMinute,
                timeLeft,
              }}
            />
          )
        default:
          return null
      }
    }, [
      activeView,
      isLastMinute,
      onMfaSetupCanceled,
      onMfaSetupCompleted,
      t,
      timeLeft,
    ])

    const title = useMemo(() => {
      switch (activeView) {
        case 'mfa_step1':
        case 'mfa_step2':
          return t('mfaSetup.step1.modalTitle')
        default:
          return t('account_settings')
      }
    }, [activeView, t])

    const headerChildren = useMemo(() => {
      switch (activeView) {
        case 'mfa_step1':
        case 'mfa_step2':
          return (
            <MfaSetupProgressBar
              {...{
                currentStep: activeView === 'mfa_step1' ? 1 : 2,
                maxSteps: 2,
                t,
              }}
            />
          )
        default:
          return undefined
      }
    }, [activeView, t])

    return (
      <Modal
        {...{
          title,
          headerChildren,
          tabs: activeView ? undefined : [t('general'), t('security')],
          onCancelButtonClick: undefined,
        }}>
        {activeView ? activeInternalView : tabView}
      </Modal>
    )
  },
)
